/**
 * PipelineProgress Component
 * Shows animated progress during site generation
 */

import { useState, useEffect, useRef } from 'react'
import {
  Search, Users, Map, FileText, Palette, Code, Image,
  Check, Loader2, ChevronDown, ChevronRight, Sparkles, File,
  AlertTriangle, Clock, Lightbulb, Globe, Zap, PartyPopper, Rocket, ExternalLink,
  Award, Shield, Gauge, Eye, Package, Github, Download
} from 'lucide-react'
import { useStore } from '@store/useStore'
import { PHASES, PHASE_INFO } from '@agents/orchestrator'
import { calculateQualityScore, getScoreColor, getScoreBgColor } from '@services/pagespeed'

// Agent icons mapping
const AGENT_ICONS = {
  [PHASES.SERP]: Globe,
  [PHASES.STRATEGE]: Search,
  [PHASES.IDENTITE]: Users,
  [PHASES.ARCHITECTE]: Map,
  [PHASES.REDACTEUR]: FileText,
  [PHASES.DA]: Palette,
  [PHASES.CONTENT]: Code,
  [PHASES.IMAGES]: Image,
}

// Agent colors - SERP has distinctive cyan/teal
const AGENT_COLORS = {
  [PHASES.SERP]: 'from-cyan-400 to-teal-500',
  [PHASES.STRATEGE]: 'from-blue-500 to-indigo-500',
  [PHASES.IDENTITE]: 'from-purple-500 to-pink-500',
  [PHASES.ARCHITECTE]: 'from-emerald-500 to-teal-500',
  [PHASES.REDACTEUR]: 'from-amber-500 to-orange-500',
  [PHASES.DA]: 'from-rose-500 to-red-500',
  [PHASES.CONTENT]: 'from-indigo-500 to-violet-500',
  [PHASES.IMAGES]: 'from-green-500 to-lime-500',
}

// Estimated time per phase (in seconds) - Updated for more context/tokens
const PHASE_ESTIMATES = {
  [PHASES.SERP]: { min: 15, max: 45 },
  [PHASES.STRATEGE]: { min: 45, max: 120 },
  [PHASES.IDENTITE]: { min: 30, max: 90 },
  [PHASES.ARCHITECTE]: { min: 30, max: 90 },
  [PHASES.REDACTEUR]: { min: 45, max: 120 },
  [PHASES.DA]: { min: 30, max: 90 },
  [PHASES.CONTENT]: { min: 90, max: 240 },
  [PHASES.IMAGES]: { min: 45, max: 180 },
}

// Phase-specific tips explaining what each agent does
const PHASE_TIPS = {
  [PHASES.SERP]: [
    "Recherche web en temps reel pour analyser les resultats actuels",
    "Identification de vos concurrents : qui sont les 10 premiers ? Leurs forces ?",
    "Extraction des 'People Also Ask' : les vraies questions de vos futurs clients",
    "Analyse des snippets et featured snippets : opportunités de position 0",
    "Cartographie du paysage SEO local : qui domine votre zone géographique ?",
  ],
  [PHASES.STRATEGE]: [
    "Décodage sémantique du NDD : extraction du métier, zone géo, spécialités...",
    "Synthèse des données SERP pour définir votre angle d'attaque",
    "Identification des intentions de recherche : informationnelle, transactionnelle, navigationnelle",
    "Cartographie des mots-clés : volume, difficulté, opportunités de ranking",
    "Définition du positionnement unique : comment vous différencier des 10 premiers résultats",
  ],
  [PHASES.IDENTITE]: [
    "Création du persona client idéal : qui cherche vos services ? Quels sont ses pain points ?",
    "Définition du ton éditorial : professionnel, accessible, expert, rassurant...",
    "Construction de la brand voice : vocabulaire, expressions, niveau de langage",
    "Élaboration des arguments de vente uniques (USP) adaptés au marché local",
    "Calibrage du niveau d'expertise affiché selon votre cible",
  ],
  [PHASES.ARCHITECTE]: [
    "Structure en silos thématiques : T1 (accueil) → T2 (catégories) → T3 (services) → T4 (détails)",
    "Maillage interne intelligent : chaque page pousse les autres stratégiquement",
    "Optimisation du crawl budget : structure plate pour faciliter l'indexation",
    "Définition des URLs SEO-friendly avec mots-clés cibles",
    "Planification des ancres de liens internes pour le jus SEO",
  ],
  [PHASES.REDACTEUR]: [
    "Briefs de contenu par page : H1, H2, H3, nombre de mots, densité keywords",
    "Génération des FAQs basées sur les 'People Also Ask' de Google",
    "Création des FLUQs : questions que vos futurs clients poseront demain",
    "Optimisation sémantique : LSI keywords, entités nommées, co-occurrences",
    "Meta titles et descriptions uniques avec CTR optimisé",
  ],
  [PHASES.DA]: [
    "Palette couleurs adaptée au secteur : psychologie des couleurs par métier",
    "Sélection typographique : lisibilité web + personnalité de marque",
    "Définition du style visuel : moderne, traditionnel, premium, accessible...",
    "Cohérence graphique : espacements, bordures, ombres, animations",
    "Adaptation mobile-first : touch targets, tailles de police, contrastes",
  ],
  [PHASES.CONTENT]: [
    "Génération HTML5 sémantique : header, main, article, section, footer",
    "Intégration Schema.org LocalBusiness : données structurées pour Google",
    "CSS optimisé : variables custom, flexbox/grid, animations GPU",
    "Balisage Hn hiérarchique : un seul H1, structure logique H2-H6",
    "Lazy loading images, async scripts, critical CSS inline",
  ],
  [PHASES.IMAGES]: [
    "Extraction des balises img du HTML généré",
    "Prompts optimisés pour Gemini Imagen / DALL-E / Midjourney",
    "Compression WebP < 100KB par image pour le Core Web Vitals",
    "Alt text SEO descriptifs et accessibles (WCAG)",
    "Dimensions responsives : srcset pour tous les breakpoints",
  ],
}

// Fallback tips if phase not found
const GENERIC_TIPS = [
  "8 agents IA travaillent en synergie pour créer votre site",
  "Chaque décision est basée sur l'analyse de vos concurrents",
  "Le site généré respecte les dernières guidelines Google",
]

// Pipeline phases in order (now includes SERP)
const PIPELINE_ORDER = [
  PHASES.SERP,
  PHASES.STRATEGE,
  PHASES.IDENTITE,
  PHASES.ARCHITECTE,
  PHASES.REDACTEUR,
  PHASES.DA,
  PHASES.CONTENT,
  PHASES.IMAGES,
]

// Format seconds to mm:ss
function formatTime(seconds) {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Compact animated working visual
function WorkingAnimation({ phase }) {
  const colors = AGENT_COLORS[phase] || 'from-primary-500 to-accent-500'
  const Icon = AGENT_ICONS[phase] || Sparkles

  return (
    <div className="relative w-16 h-16 mx-auto">
      {/* Spinning ring */}
      <div
        className={`absolute inset-0 rounded-full bg-gradient-to-r ${colors} opacity-20 animate-spin`}
        style={{ animationDuration: '2s' }}
      />

      {/* Pulsing glow */}
      <div
        className={`absolute inset-1 rounded-full bg-gradient-to-r ${colors} opacity-30 animate-pulse blur-sm`}
      />

      {/* Center icon */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colors} flex items-center justify-center shadow-lg`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </div>
  )
}

// Get readable highlights from result
function getResultHighlights(phase, result) {
  if (!result) return []

  const highlights = []

  switch (phase) {
    case PHASES.SERP:
      const providerNames = { perplexity: 'Perplexity', claude: 'Claude', openai: 'GPT', gemini: 'Gemini' }
      const searchProvider = result.searchProvider || 'auto'
      const providerLabel = providerNames[searchProvider] || searchProvider
      if (result.configured !== undefined) highlights.push({ label: 'Provider', value: result.configured ? providerLabel : 'Non configure' })
      if (result.serp?.sources?.length) highlights.push({ label: 'Sources', value: `${result.serp.sources.length}` })
      if (Array.isArray(result.keywords) && result.keywords.length) {
        highlights.push({ label: 'Keywords', value: result.keywords.slice(0, 3).join(', ') })
      }
      break
    case PHASES.STRATEGE:
      if (result.domain?.service) highlights.push({ label: 'Service', value: result.domain.service })
      if (result.domain?.location) highlights.push({ label: 'Zone', value: result.domain.location })
      if (result.strategy?.positioning) highlights.push({ label: 'Positionnement', value: result.strategy.positioning })
      if (result.keywords?.primary) highlights.push({ label: 'Mot-clé principal', value: result.keywords.primary })
      if (result.competitors?.length) highlights.push({ label: 'Concurrents analysés', value: `${result.competitors.length}` })
      break
    case PHASES.IDENTITE:
      if (result.persona?.name) highlights.push({ label: 'Persona', value: result.persona.name })
      if (result.tone?.style) highlights.push({ label: 'Ton', value: result.tone.style })
      if (result.brandVoice) highlights.push({ label: 'Voix', value: result.brandVoice })
      if (result.usp) highlights.push({ label: 'USP', value: result.usp })
      break
    case PHASES.ARCHITECTE:
      const pages = result.pages || result.architecture?.pages || []
      if (pages.length) {
        highlights.push({ label: 'Pages', value: `${pages.length}` })
        pages.slice(0, 4).forEach(p => {
          highlights.push({ label: '', value: `→ ${p.name || p.title || p.slug}`, small: true })
        })
        if (pages.length > 4) highlights.push({ label: '', value: `+ ${pages.length - 4} autres...`, small: true })
      }
      break
    case PHASES.REDACTEUR:
      if (result.briefs) {
        const briefKeys = Object.keys(result.briefs)
        highlights.push({ label: 'Briefs', value: `${briefKeys.length}` })
      }
      if (result.faqs?.length) highlights.push({ label: 'FAQs', value: `${result.faqs.length} questions` })
      break
    case PHASES.DA:
      if (result.colors?.primary) highlights.push({ label: 'Couleur principale', value: result.colors.primary, color: result.colors.primary })
      if (result.colors?.secondary) highlights.push({ label: 'Secondaire', value: result.colors.secondary, color: result.colors.secondary })
      if (result.typography?.heading) highlights.push({ label: 'Typo titres', value: result.typography.heading })
      if (result.style) highlights.push({ label: 'Style', value: result.style })
      break
    case PHASES.CONTENT:
      if (result.files) {
        const fileKeys = Object.keys(result.files)
        highlights.push({ label: 'Fichiers HTML', value: `${fileKeys.length}` })
        fileKeys.slice(0, 3).forEach(f => {
          highlights.push({ label: '', value: `→ ${f}`, small: true })
        })
      }
      break
    case PHASES.IMAGES:
      if (result.images?.length) {
        highlights.push({ label: 'Images', value: `${result.images.length}` })
      }
      break
  }

  return highlights
}

// Compact agent item for anchored layout
function AgentItem({ phase, status, result, summary, isExpanded, onToggle, compact = false }) {
  const info = PHASE_INFO[phase]
  const Icon = AGENT_ICONS[phase] || Sparkles
  const colors = AGENT_COLORS[phase]

  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <Check className="w-3 h-3 text-emerald-400" />
      case 'running':
        return <Loader2 className="w-3 h-3 text-primary-400 animate-spin" />
      default:
        return <div className="w-3 h-3 rounded-full border border-slate-600" />
    }
  }

  const highlights = getResultHighlights(phase, result)
  const hasDetails = result || summary

  // Compact mode with AI summary display
  if (compact) {
    const hasSummary = summary?.headline || summary?.bullets?.length > 0

    return (
      <div
        className={`rounded-lg transition-all overflow-hidden ${
          status === 'running'
            ? 'bg-primary-500/10 ring-1 ring-primary-500/30'
            : status === 'completed'
              ? hasSummary ? 'bg-gradient-to-br from-slate-800/50 to-slate-800/30 border border-slate-700/50' : 'bg-slate-800/30'
              : 'opacity-50 bg-slate-900/20'
        }`}
      >
        {/* Header row */}
        <div
          className="flex items-center gap-2 px-2 py-1.5 cursor-pointer"
          onClick={hasDetails ? onToggle : undefined}
        >
          <div
            className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${
              status === 'completed'
                ? `bg-gradient-to-br ${colors}`
                : status === 'running'
                  ? `bg-gradient-to-br ${colors} animate-pulse`
                  : 'bg-slate-800'
            }`}
          >
            <Icon className={`w-3 h-3 ${status !== 'pending' ? 'text-white' : 'text-slate-500'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <span className={`text-xs font-medium block truncate ${status === 'pending' ? 'text-slate-500' : 'text-white'}`}>
              {summary?.headline || info?.name}
            </span>
            {summary?.keyMetric && (
              <span className="text-[9px] text-primary-400 block truncate">
                {summary.keyMetric.label}: {summary.keyMetric.value}
              </span>
            )}
          </div>
          {getStatusIcon()}
          {hasDetails && status === 'completed' && (
            isExpanded ? <ChevronDown className="w-3 h-3 text-slate-500" /> : <ChevronRight className="w-3 h-3 text-slate-500" />
          )}
        </div>

        {/* AI Summary bullets (shown when expanded) */}
        {isExpanded && hasSummary && (
          <div className="px-2 pb-2 space-y-1">
            {summary.bullets?.slice(0, 3).map((bullet, idx) => (
              <div key={idx} className="flex items-start gap-1.5 text-[10px] text-slate-400 pl-1">
                <span className="text-primary-400 flex-shrink-0">{bullet.match(/^[^\s]+/)?.[0] || '→'}</span>
                <span className="truncate">{bullet.replace(/^[^\s]+\s*/, '')}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className={`rounded-lg border transition-all ${
        status === 'running'
          ? 'border-primary-500/50 bg-primary-500/5'
          : status === 'completed'
            ? 'border-slate-700/50 bg-slate-800/30'
            : 'border-slate-800/50 bg-slate-900/30'
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 p-2"
        disabled={!hasDetails}
      >
        {/* Status icon */}
        <div className="flex-shrink-0">
          {getStatusIcon()}
        </div>

        {/* Agent icon */}
        <div
          className={`w-7 h-7 rounded-lg flex items-center justify-center ${
            status === 'completed'
              ? `bg-gradient-to-br ${colors}`
              : status === 'running'
                ? `bg-gradient-to-br ${colors} animate-pulse`
                : 'bg-slate-800'
          }`}
        >
          <Icon className={`w-3.5 h-3.5 ${status !== 'pending' ? 'text-white' : 'text-slate-500'}`} />
        </div>

        {/* Name */}
        <div className="flex-1 text-left">
          <div className={`font-medium text-xs ${status === 'pending' ? 'text-slate-500' : 'text-white'}`}>
            {info?.name}
          </div>
        </div>

        {/* View details button */}
        {hasDetails && (
          <div className={`flex-shrink-0 ${isExpanded ? 'text-primary-400' : 'text-slate-500'}`}>
            {isExpanded ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
          </div>
        )}
      </button>

      {/* Expanded details */}
      {isExpanded && hasDetails && (
        <div className="px-2 pb-2 pt-0">
          <div className="bg-slate-900/80 rounded p-2 space-y-1">
            {highlights.slice(0, 4).map((h, idx) => (
              <div key={idx} className={`flex items-center gap-2 ${h.small ? 'pl-3' : ''}`}>
                {h.label && (
                  <span className="text-[10px] text-slate-500 min-w-[80px]">{h.label}</span>
                )}
                <span className={`text-[10px] ${h.small ? 'text-slate-500' : 'text-slate-300'} truncate`}>
                  {h.color && (
                    <span
                      className="inline-block w-2 h-2 rounded mr-1 align-middle"
                      style={{ backgroundColor: h.color }}
                    />
                  )}
                  {h.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default function PipelineProgress() {
  const { project, progress, ui, files, summaries } = useStore()
  const [elapsedTime, setElapsedTime] = useState(0)
  const [totalElapsed, setTotalElapsed] = useState(0)
  const [currentTip, setCurrentTip] = useState(0)
  const timerRef = useRef(null)
  const lastPhaseRef = useRef(null)

  const currentPhase = progress.phase || project.phase
  const isRunning = ui.isLoading
  const fileCount = Object.keys(files).length
  const isComplete = !isRunning && fileCount > 0

  // Calculate quality score for completed sites
  const qualityScore = isComplete ? calculateQualityScore(files) : null

  // Timer for current phase
  useEffect(() => {
    if (isRunning && currentPhase) {
      // Reset timer when phase changes
      if (lastPhaseRef.current !== currentPhase) {
        setElapsedTime(0)
        lastPhaseRef.current = currentPhase
      }

      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1)
        setTotalElapsed(prev => prev + 1)
      }, 1000)

      return () => clearInterval(timerRef.current)
    } else {
      clearInterval(timerRef.current)
    }
  }, [isRunning, currentPhase])

  // Get tips for current phase
  const currentPhaseTips = currentPhase ? (PHASE_TIPS[currentPhase] || GENERIC_TIPS) : GENERIC_TIPS

  // Rotate tips every 4 seconds, reset when phase changes
  useEffect(() => {
    if (isRunning) {
      setCurrentTip(0) // Reset to first tip when phase changes
      const tipInterval = setInterval(() => {
        setCurrentTip(prev => (prev + 1) % currentPhaseTips.length)
      }, 4000)
      return () => clearInterval(tipInterval)
    }
  }, [isRunning, currentPhase, currentPhaseTips.length])

  // Reset total elapsed when starting new generation
  useEffect(() => {
    if (isRunning && !lastPhaseRef.current) {
      setTotalElapsed(0)
    }
  }, [isRunning])

  // Determine status for each phase
  const getPhaseStatus = (phase) => {
    const phaseIndex = PIPELINE_ORDER.indexOf(phase)
    const currentIndex = PIPELINE_ORDER.indexOf(currentPhase)

    if (phase === currentPhase && isRunning) return 'running'
    if (phaseIndex < currentIndex || project.context[phase]) return 'completed'
    return 'pending'
  }

  // Calculate overall progress
  const completedCount = PIPELINE_ORDER.filter(
    (p) => getPhaseStatus(p) === 'completed'
  ).length
  const progressPercent = (completedCount / PIPELINE_ORDER.length) * 100

  // No pipeline running and no context - show idle state
  if (!isRunning && !project.domain) {
    return (
      <div className="flex flex-col h-full items-center justify-center p-6 text-center">
        <div className="w-20 h-20 rounded-2xl bg-slate-800/50 flex items-center justify-center mb-4">
          <Sparkles className="w-10 h-10 text-slate-600" />
        </div>
        <h3 className="text-lg font-medium text-slate-400 mb-2">
          En attente d'un projet
        </h3>
        <p className="text-sm text-slate-500">
          Entre un domaine EMD pour démarrer le pipeline de génération
        </p>
      </div>
    )
  }

  // SUCCESS STATE - Site generated!
  // Just show compact success banner + stats, AgentSummaryStack below shows details
  if (isComplete) {
    return (
      <div className="flex flex-col">
        {/* Success banner - compact */}
        <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border-b border-emerald-500/30 p-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/25">
              <PartyPopper className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-bold text-emerald-300">Site généré !</h3>
              <p className="text-xs text-emerald-400/80">{fileCount} pages HTML prêtes</p>
            </div>
            {qualityScore && (
              <div className={`text-center px-3 py-1.5 rounded-lg border ${getScoreBgColor(qualityScore.score)}`}>
                <div className={`text-lg font-bold ${getScoreColor(qualityScore.score)}`}>
                  {qualityScore.grade.letter}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Compact stats row */}
        <div className="p-3 border-b border-slate-800 flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs">
            <span className="text-slate-500">Pages:</span>
            <span className="font-bold text-white">{fileCount}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <span className="text-slate-500">Agents:</span>
            <span className="font-bold text-white">{completedCount}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <span className="text-slate-500">Temps:</span>
            <span className="font-bold text-white font-mono">{totalElapsed > 0 ? formatTime(totalElapsed) : '--:--'}</span>
          </div>
        </div>

        {/* Action button */}
        <div className="p-3">
          <button
            onClick={() => useStore.getState().setRightPanel('pages')}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-accent-600 hover:from-primary-500 hover:to-accent-500 rounded-lg font-medium text-sm transition-all"
          >
            <Rocket className="w-4 h-4" />
            Voir les pages
          </button>
        </div>
      </div>
    )
  }

  // Get estimate for current phase
  const currentEstimate = currentPhase ? PHASE_ESTIMATES[currentPhase] : null

  // Only show warning banner when running, progress is in AgentSummaryStack
  if (!isRunning) return null

  return (
    <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 py-2 flex items-center gap-2">
      <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
      <p className="text-xs text-amber-300">
        <span className="font-semibold">Ne pas rafraîchir !</span> La génération est en cours.
      </p>
    </div>
  )
}
